import { useEffect, useState } from "react";
import { Box, Flex, Text, Image } from "@chakra-ui/react";
import { motion, useAnimationControls } from "framer-motion";
import PickerIcon from "../assets/images/picker.png";

interface CircularPickerProps {
  items: string[];
  initialIndex?: number;
}

export const CircularPicker = ({ items, initialIndex = 0 }: CircularPickerProps) => {
  const controls = useAnimationControls();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const itemHeight = 72; // Height of each item
  const visibleItems = 9; // Total items visible at once

  // Create extended array for seamless infinite scroll
  const extendedItems = [...items, ...items, ...items];
  const centerIndex = items.length + currentIndex;

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const autoScroll = async () => {
      // Scroll to next item
      setCurrentIndex((prev) => (prev + 1) % items.length);
    };

    // Auto-scroll every 3 seconds
    intervalId = setInterval(autoScroll, 3000);

    return () => clearInterval(intervalId);
  }, [items.length]);

  useEffect(() => {
    // Animate to current position
    const targetY = -(centerIndex * itemHeight) + (visibleItems / 2) * itemHeight;
    controls.start({
      y: targetY,
      transition: {
        duration: 0.6,
        ease: "easeInOut",
      },
    });
  }, [currentIndex, controls, centerIndex, itemHeight, visibleItems]);

  const getOpacity = (index: number) => {
    const distance = Math.abs(index - centerIndex);
    if (distance === 0) return 1;
    if (distance === 1) return 0.6;
    if (distance === 2) return 0.4;
    if (distance === 3) return 0.25;
    return 0.15;
  };

  const getScale = (index: number) => {
    const distance = Math.abs(index - centerIndex);
    if (distance === 0) return 1;
    return 0.95;
  };

  return (
    <Box
      position="relative"
      h="100%"
      w="100%"
      overflow="hidden"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {/* Container for items */}
      <Box
        position="relative"
        h={`${visibleItems * itemHeight}px`}
        w="100%"
        maxW="600px"
        overflow="hidden"
      >
        <motion.div
          animate={controls}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          {extendedItems.map((item, index) => {
            const isCenter = index === centerIndex;
            const opacity = getOpacity(index);
            const scale = getScale(index);

            return (
              <Flex
                key={`${item}-${index}`}
                h={`${itemHeight}px`}
                w="100%"
                align="center"
                justify="center"
                position="relative"
                opacity={opacity}
                transform={`scale(${scale})`}
                transition="opacity 0.3s ease, transform 0.3s ease"
              >
                {isCenter && (
                  <Box
                    position="absolute"
                    bg="rgba(255, 255, 255, 0.2)"
                    backdropFilter="blur(40px)"
                    borderRadius="60px"
                    border="1px solid rgba(255, 255, 255, 0.3)"
                    px={{ base: "24px", lg: "40px" }}
                    py={{ base: "12px", lg: "18px" }}
                    w="fit-content"
                    minW={{ base: "280px", lg: "380px" }}
                    display="flex"
                    alignItems="center"
                    gap="16px"
                    boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.15)"
                  >
                    <Image
                      src={PickerIcon}
                      alt="picker"
                      w="40px"
                      h="40px"
                      objectFit="contain"
                    />
                    <Text
                      fontSize={{ base: "24px", lg: "32px" }}
                      fontWeight={500}
                      color="#19202D"
                      fontFamily="LeagueSpartan"
                      lineHeight="100%"
                    >
                      {item}
                    </Text>
                  </Box>
                )}
                {!isCenter && (
                  <Text
                    fontSize={{ base: "24px", lg: "32px" }}
                    fontWeight={400}
                    color="#19202D"
                    fontFamily="LeagueSpartan"
                    lineHeight="100%"
                  >
                    {item}
                  </Text>
                )}
              </Flex>
            );
          })}
        </motion.div>
      </Box>
    </Box>
  );
};
